import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { useEventsApi } from '../services/eventsApi'
import PoolsApi from '../services/poolsApi'
import { useRailbirdGamesApi } from '../services/railbirdGamesApi'
import { useRailbirdLedgerApi } from '../services/railbirdLedgerApi'
import HeroVideo from '../components/HeroVideo'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { 
  CalendarIcon, 
  TrophyIcon,
  UserGroupIcon,
  ClockIcon,
  ArrowRightIcon,
  ChartBarIcon,
  HeartIcon,
  PhotoIcon,
  ArchiveBoxIcon,
  RectangleGroupIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import { formatDateLocal, formatDateRange, formatTime } from '../utils/dateUtils'

// Dashboard data interfaces
interface DashboardEvent {
  id: string
  title: string
  description: string
  startDate: string
  endDate?: string
  time: string
  isMultiDay: boolean
}

interface DashboardPool {
  id: string
  name: string
  type: 'horse-racing' | 'football' | 'cornhole'
  status: 'open' | 'closed' | 'completed'
  participantCount: number
  totalPot?: number
  nextRace?: string
  nextGame?: string
  week?: number
  pickDeadline?: string
  entryFee?: number
  maxParticipants?: number
}

export default function DashboardPage() {
  const auth0 = useAuth0()
  const { user, isAuthenticated, getAccessTokenSilently } = auth0
  const eventsApi = useEventsApi()
  const railbirdGamesApi = useRailbirdGamesApi()
  const railbirdLedgerApi = useRailbirdLedgerApi()
  
  // TanStack Query for events
  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ['events'],
    queryFn: () => eventsApi.getEvents(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: isAuthenticated,
    onError: () => toast.error('Failed to load events')
  })
  
  // TanStack Query for pools
  const { data: poolsData, isLoading: poolsLoading } = useQuery({
    queryKey: ['pools'],
    queryFn: async () => {
      const poolsApiInstance = new PoolsApi(auth0)
      return poolsApiInstance.getPools()
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: isAuthenticated,
    onError: () => toast.error('Failed to load pools')
  })

  // TanStack Query for latest Railbird Game
  const { data: gamesData, isLoading: gamesLoading } = useQuery({
    queryKey: ['railbird-games-latest'],
    queryFn: () => railbirdGamesApi.getGames(getAccessTokenSilently, { limit: 1 }),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: isAuthenticated,
    onError: () => toast.error('Failed to load games')
  })

  // TanStack Query for latest Ledger entry
  const { data: ledgerData, isLoading: ledgerLoading } = useQuery({
    queryKey: ['railbird-ledger-latest'],
    queryFn: () => railbirdLedgerApi.getEntries({ limit: 1 }),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: isAuthenticated,
    onError: () => toast.error('Failed to load ledger entries')
  })

  const loading = eventsLoading || poolsLoading || gamesLoading || ledgerLoading

  // Transform data for dashboard
  const latestEvent: DashboardEvent | null = (() => {
    if (!eventsData?.events) return null
    
    const upcomingEvents = eventsData.events
      .filter(event => {
        // Use the primary date field (eventDate or startDate)
        const eventDate = event.eventDate || event.startDate
        if (!eventDate) return false
        
        // Parse date without timezone conversion
        const [year, month, day] = eventDate.split('-').map(Number)
        const eventDateObj = new Date(year, month - 1, day)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        
        return eventDateObj >= today
      })
      .sort((a, b) => {
        const dateA = a.eventDate || a.startDate
        const dateB = b.eventDate || b.startDate
        return dateA.localeCompare(dateB)
      })

    if (upcomingEvents.length === 0) return null

    const nextEvent = upcomingEvents[0]
    
    return {
      id: nextEvent.eventId,
      title: nextEvent.title,
      description: nextEvent.description,
      startDate: nextEvent.eventDate || nextEvent.startDate,
      endDate: nextEvent.endDate,
      time: nextEvent.time,
      isMultiDay: nextEvent.isMultiDay
    }
  })()

  const horsePool: DashboardPool | null = (() => {
    if (!poolsData?.pools) return null
    
    const activePools = poolsData.pools.filter(pool => pool.status === 'open' || pool.status === 'active')
    const horseRacingPool = activePools.find(pool => pool.type === 'horse-racing')
    
    if (!horseRacingPool) return null
    
    return {
      id: horseRacingPool.poolId,
      name: horseRacingPool.name,
      type: 'horse-racing',
      status: horseRacingPool.status as any,
      participantCount: horseRacingPool.participants?.length || 0,
      totalPot: horseRacingPool.entryFee ? (horseRacingPool.participants?.length || 0) * horseRacingPool.entryFee : undefined,
      nextRace: horseRacingPool.nextEvent
    }
  })()

  const footballPool: DashboardPool | null = (() => {
    if (!poolsData?.pools) return null
    
    const activePools = poolsData.pools.filter(pool => pool.status === 'active' && pool.type === 'football')
    
    if (activePools.length === 0) return null
    
    // Find pool with upcoming deadline (not passed yet), sorted by soonest deadline first
    const poolsWithUpcomingDeadlines = activePools
      .filter(p => p.pickDeadline && new Date(p.pickDeadline) > new Date())
      .sort((a, b) => new Date(a.pickDeadline!).getTime() - new Date(b.pickDeadline!).getTime())
    
    // Use pool with soonest deadline, or first active pool if none have upcoming deadlines
    const fbPool = poolsWithUpcomingDeadlines[0] || activePools[0]
    
    return {
      id: fbPool.poolId,
      name: fbPool.name,
      type: 'football',
      status: fbPool.status as any,
      participantCount: fbPool.currentParticipants || 0,
      totalPot: fbPool.entryFee ? fbPool.currentParticipants * fbPool.entryFee : undefined,
      pickDeadline: fbPool.pickDeadline,
      entryFee: fbPool.entryFee,
      maxParticipants: fbPool.maxParticipants
    }
  })()

  // Get latest Railbird Game
  const latestGame = gamesData?.games?.[0] || null

  // Get latest Ledger entry
  const latestLedgerEntry = ledgerData?.entries?.[0] || null

  // Helper function to format the date display for the dashboard
  const formatEventDate = (event: DashboardEvent) => {
    if (event.isMultiDay && event.endDate && event.startDate !== event.endDate) {
      return formatDateRange(event.startDate, event.endDate)
    }
    return formatDateLocal(event.startDate)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-rb-dark">
        <HeroVideo 
          title="Welcome to Midwest Railbirds"
          subtitle="Your gateway to the world of horse racing"
          height="md"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-rb-dark">
      <HeroVideo 
        title="Welcome to Midwest Railbirds"
        subtitle="Your gateway to the world of horse racing"
        height="md"
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Welcome Message */}
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">
              {user ? `Welcome back, ${user.firstName || 'Railbird'}!` : 'Welcome to Midwest Railbirds'}
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Stay connected with fellow racing enthusiasts, join exciting events, and participate in thrilling betting pools.
            </p>
          </div>

          {/* Dashboard Cards */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {/* Next Event Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-theme-primary">Next Event</h3>
                <CalendarIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              
              {latestEvent ? (
                <div className="space-y-3">
                  <h4 className="font-medium text-theme-primary truncate">{latestEvent.title}</h4>
                  <p className="text-sm text-theme-secondary line-clamp-2">{latestEvent.description}</p>
                  
                  <div className="space-y-2 text-sm text-theme-secondary">
                    <div className="flex items-center">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {formatEventDate(latestEvent)}
                    </div>
                    {latestEvent.time && (
                      <div className="flex items-center">
                        <ClockIcon className="w-4 h-4 mr-2" />
                        {latestEvent.time === 'Invalid Date' ? 'TBD' : formatTime(latestEvent.time)}
                      </div>
                    )}
                  </div>
                  
                  <Link 
                    to={`/events/${latestEvent.id}`}
                    className="inline-flex items-center text-primary-400 hover:text-primary-300 text-sm font-medium mt-4"
                  >
                    View Details <ArrowRightIcon className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              ) : (
                <div className="text-center py-8">
                  <CalendarIcon className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-theme-tertiary text-sm">No upcoming events</p>
                  <Link 
                    to="/events"
                    className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium mt-2"
                  >
                    View All Events <ArrowRightIcon className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              )}
            </motion.div>

            {/* Horse Racing Pool Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-theme-primary">Horse Racing Pool</h3>
                <TrophyIcon className="w-6 h-6 text-yellow-500 dark:text-yellow-400" />
              </div>
              
              {horsePool ? (
                <div className="space-y-3">
                  <h4 className="font-medium text-gray-900 dark:text-white">{horsePool.name}</h4>
                  
                  <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                    <div className="flex justify-between">
                      <span>Participants:</span>
                      <span className="text-gray-900 dark:text-white">{horsePool.participantCount}</span>
                    </div>
                    {horsePool.totalPot && (
                      <div className="flex justify-between">
                        <span>Total Pot:</span>
                        <span className="text-green-600 dark:text-green-400 font-medium">${horsePool.totalPot.toLocaleString()}</span>
                      </div>
                    )}
                    {horsePool.nextRace && (
                      <div className="flex justify-between">
                        <span>Next Race:</span>
                        <span className="text-gray-900 dark:text-white">{horsePool.nextRace}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Status:</span>
                      <span className={`capitalize font-medium ${
                        horsePool.status === 'open' ? 'text-green-600 dark:text-green-400' : 
                        horsePool.status === 'closed' ? 'text-yellow-600 dark:text-yellow-400' : 
                        'text-gray-600 dark:text-gray-400'
                      }`}>
                        {horsePool.status}
                      </span>
                    </div>
                  </div>
                  
                  <Link 
                    to={`/pools/${horsePool.id}`}
                    className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium mt-4"
                  >
                    View Pool <ArrowRightIcon className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              ) : (
                <div className="text-center py-8">
                  <TrophyIcon className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-theme-tertiary text-sm">No active horse racing pools</p>
                  <Link 
                    to="/pools"
                    className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium mt-2"
                  >
                    View All Pools <ArrowRightIcon className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              )}
            </motion.div>

            {/* Football Pool Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-theme-primary">Football Pool</h3>
                <TrophyIcon className="w-6 h-6 text-orange-500 dark:text-orange-400" />
              </div>
              
              {footballPool ? (
                <div className="space-y-3">
                  <h4 className="font-medium text-white">{footballPool.name}</h4>
                  
                  <div className="space-y-2 text-sm text-gray-300">
                    <div className="flex justify-between">
                      <span>Participants:</span>
                      <span className="text-white">{footballPool.participantCount}</span>
                    </div>
                    {footballPool.totalPot && (
                      <div className="flex justify-between">
                        <span>Total Pot:</span>
                        <span className="text-green-400 font-medium">${footballPool.totalPot.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Entry Fee:</span>
                      <span className="text-white">${footballPool.entryFee}</span>
                    </div>
                    {footballPool.pickDeadline && (
                      <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-2 mt-2">
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center text-yellow-400">
                            <ClockIcon className="w-3 h-3 mr-1" />
                            Pick Deadline
                          </div>
                          <span className="text-yellow-300 font-medium">
                            {new Date(footballPool.pickDeadline).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Status:</span>
                      <span className={`capitalize font-medium ${
                        footballPool.status === 'open' || footballPool.status === 'active' ? 'text-green-400' : 
                        footballPool.status === 'closed' ? 'text-yellow-400' : 
                        'text-gray-600 dark:text-gray-400'
                      }`}>
                        {footballPool.status === 'active' ? 'open' : footballPool.status}
                      </span>
                    </div>
                  </div>
                  
                  <Link 
                    to={`/pools/${footballPool.id}`}
                    className="inline-flex items-center text-primary-400 hover:text-primary-300 text-sm font-medium mt-4"
                  >
                    View Pool <ArrowRightIcon className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              ) : (
                <div className="text-center py-8">
                  <TrophyIcon className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-theme-tertiary text-sm">No active football pools</p>
                  <Link 
                    to="/pools"
                    className="inline-flex items-center text-primary-400 hover:text-primary-300 text-sm font-medium mt-2"
                  >
                    View All Pools <ArrowRightIcon className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              )}
            </motion.div>

            {/* Latest Railbird Game Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-theme-primary">Latest Game</h3>
                <RectangleGroupIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              
              {latestGame ? (
                <div className="space-y-3">
                  <h4 className="font-medium text-theme-primary truncate">{latestGame.name}</h4>
                  <p className="text-sm text-theme-secondary line-clamp-2">{latestGame.description}</p>
                  
                  <div className="space-y-2 text-sm text-theme-secondary">
                    <div className="flex justify-between">
                      <span>Max Players:</span>
                      <span className="text-theme-primary">{latestGame.maxPlayers}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tales:</span>
                      <span className="text-theme-primary">{latestGame.talesFromTheRail.length}</span>
                    </div>
                  </div>
                  
                  <Link 
                    to={`/legacy/railbird-games/${latestGame.id}`}
                    className="inline-flex items-center text-primary-400 hover:text-primary-300 text-sm font-medium mt-4"
                  >
                    View Game <ArrowRightIcon className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              ) : (
                <div className="text-center py-8">
                  <RectangleGroupIcon className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-theme-tertiary text-sm">No games yet</p>
                  <Link 
                    to="/legacy/railbird-games"
                    className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium mt-2"
                  >
                    View All Games <ArrowRightIcon className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              )}
            </motion.div>

            {/* Latest Ledger Entry Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-theme-primary">Latest Adventure</h3>
                <BookOpenIcon className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              
              {latestLedgerEntry ? (
                <div className="space-y-3">
                  <h4 className="font-medium text-theme-primary truncate">{latestLedgerEntry.title}</h4>
                  <p className="text-sm text-theme-secondary line-clamp-3">{latestLedgerEntry.body}</p>
                  
                  <div className="space-y-2 text-sm text-theme-secondary">
                    <div className="flex justify-between">
                      <span>Scribe:</span>
                      <span className="text-theme-primary">{latestLedgerEntry.scribe}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Date:</span>
                      <span className="text-theme-primary">{formatDateLocal(latestLedgerEntry.createdAt)}</span>
                    </div>
                  </div>
                  
                  <Link 
                    to="/legacy/ledger"
                    className="inline-flex items-center text-primary-400 hover:text-primary-300 text-sm font-medium mt-4"
                  >
                    View Ledger <ArrowRightIcon className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              ) : (
                <div className="text-center py-8">
                  <BookOpenIcon className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-theme-tertiary text-sm">No adventures yet</p>
                  <Link 
                    to="/legacy/ledger"
                    className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium mt-2"
                  >
                    View Ledger <ArrowRightIcon className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              )}
            </motion.div>
          </div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg border border-gray-200 dark:border-gray-700"
          >
            <h3 className="text-lg font-semibold text-theme-primary mb-4">Quick Actions</h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              <Link 
                to="/legacy"
                className="flex items-center p-4 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <ArchiveBoxIcon className="w-6 h-6 text-green-500 dark:text-green-400 mr-3" />
                <div>
                  <p className="text-theme-primary font-medium">Legacy</p>
                  <p className="text-theme-tertiary text-sm">Club history & archives</p>
                </div>
              </Link>
              <Link 
                to="/legacy/roster"
                className="flex items-center p-4 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <ChartBarIcon className="w-6 h-6 text-blue-500 dark:text-blue-400 mr-3" />
                <div>
                  <p className="text-theme-primary font-medium">Historic Roster</p>
                  <p className="text-theme-tertiary text-sm">Member attendance records</p>
                </div>
              </Link>
              
              <Link 
                to="/legacy/kazy-cup"
                className="flex items-center p-4 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <TrophyIcon className="w-6 h-6 text-yellow-500 dark:text-yellow-400 mr-3" />
                <div>
                  <p className="text-theme-primary font-medium">Kazy Cup Champions</p>
                  <p className="text-theme-tertiary text-sm">Annual award winners</p>
                </div>
              </Link>
              
              <Link 
                to="/legacy/memorial"
                className="flex items-center p-4 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <HeartIcon className="w-6 h-6 text-red-500 dark:text-red-400 mr-3" />
                <div>
                  <p className="text-theme-primary font-medium">Memorial Section</p>
                  <p className="text-theme-tertiary text-sm">Remembering our members</p>
                </div>
              </Link>
              
              <Link 
                to="/legacy/gallery"
                className="flex items-center p-4 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <PhotoIcon className="w-6 h-6 text-purple-500 dark:text-purple-400 mr-3" />
                <div>
                  <p className="text-theme-primary font-medium">Photo Gallery</p>
                  <p className="text-theme-tertiary text-sm">Memories from years past</p>
                </div>
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}